import { useState } from 'react';
import {
  numToStr,
  toMillisecond,
  toSecond,
  getUnitOfSpecie,
} from '../../../lib/common.helpers';
import { ISpatStorageResource } from '../../../entities/farms.entities';
import {
  Button,
  CloseIcon,
  Datepicker,
  Dropdown,
  Input,
  RadioButton,
  Subtitle,
} from '../../shared';
import { Modal, Radio, Table } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { selectLang } from '../../../store/ui/ui.selector';
import { componentTranslate, translate } from '../../../lib/lang.helper';
import { selectFarmsData } from '../../../store/farms/farms.selector';
import { selectSeasons } from '../../../store/utils/utils.selector';
import {
  selectAccount,
  selectProfile,
} from '../../../store/auth/auth.selector';
import MinMaxInputForm from '../SeedingModal/MinMaxInputForm';
import ModalFeedbackView from '../../shared/feedback/ModalFeedbackView';
import { showFeedback } from '../../../store/ui/ui.actions';
import { sendSingleRequest } from '../../../apis';
import { loadSpatStorages } from '../../../store/farms/farms.actions';
import SelectBoat from '../../farmform/SelectBoat';

interface ILineForm {
  line_id: number;
  spat_amount: number | null;
  line_length: number | null;
}

interface IFormData {
  lines: ILineForm[];
  season_name: string;
  boat_id?: number | null;
  planned_date_seed: number;
  planned_date_harvest: number;
  spat_size: number | null;
  spat_size_max: number | null;
  density: number | null;
  density_max: number | null;
  density_unit: 'spat per m' | 'kgs per m';
  drop: number | null;
  spacing: number | null;
  submersion: number | null;
  comment: string | null;
}

const defaultForm: IFormData = {
  lines: [],
  season_name: '',
  boat_id: null,
  planned_date_seed: Date.now(),
  planned_date_harvest: Date.now(),
  spat_size: null,
  spat_size_max: null,
  density: null,
  density_max: null,
  density_unit: 'spat per m',
  drop: null,
  spacing: null,
  submersion: null,
  comment: null,
};

interface Props {
  data?: any;
  visible: boolean;
  spatStorage: ISpatStorageResource;
  onClose: (data?: any) => void;
  overConfirm?: (data: any) => Promise<any>;
  className?: string;
}

const MusselBulkSeedModal = ({
  data,
  visible,
  spatStorage,
  onClose,
  overConfirm,
  className,
}: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const farmsData = useSelector(selectFarmsData);
  const linesData = farmsData
    .flatMap(farm => farm.lines)
    .filter(x => x.crop_type === 'MUSSEL');
  const seasons = useSelector(selectSeasons);
  const curBoat = useSelector(selectProfile)?.current_boat;
  const defaultDropperLength =
    useSelector(selectAccount)?.default_dropper_length;
  const unitType = useSelector(selectAccount)?.display_unit;

  const getDensityUnitSpecie = (densityUnit: 'spat per m' | 'kgs per m') =>
    densityUnit === 'kgs per m' ? 'kg_per_m' : 'spat_mer_m';

  const [formData, setFormData] = useState<IFormData>(
    data
      ? {
          ...defaultForm,
          ...data,
          boat_id: data?.boat_id ?? curBoat?.id,
          planned_date_seed: toMillisecond(
            data?.planned_date_seed ?? defaultForm.planned_date_seed,
          ),
          planned_date_harvest: toMillisecond(
            data?.planned_date_harvest ?? defaultForm.planned_date_harvest,
          ),
          drop: data?.drop as number | null,
          spacing: data?.spacing as number | null,
          submersion: data?.submersion as number | null,
          spat_size: data?.spat_size as number | null,
          spat_size_max: data?.spat_size_max as number | null,
          density: data?.density as number | null,
          density_max: data?.density_max as number | null,
          lines: data?.lines ?? [],
        }
      : {
          ...defaultForm,
          boat_id: curBoat?.id,
          drop: defaultDropperLength ?? defaultForm.drop,
          spat_size: spatStorage.harvest?.shell_length as number | null,
          spat_size_max: spatStorage.harvest?.shell_length_max as number | null,
        },
  );
  const [disabled, setDisabled] = useState(false);

  const linesOption = linesData.map(x => ({
    id: x.id.toString(),
    value: x.id.toString(),
    label: `${x.farm_name} (${x.farm_number}) - ${x.line_name}`,
  }));

  const selectLines = (lines: string[]) => {
    let newLines = formData.lines.filter(x =>
      lines.includes(x.line_id.toString()),
    );
    for (let idStr of lines) {
      const line = linesData.find(x => x.id === Number(idStr));
      if (!line) continue;

      const i = newLines.findIndex(x => x.line_id === line.id);
      if (i < 0) {
        newLines.push({
          line_id: line.id,
          spat_amount: null,
          line_length: null,
        });
      }
    }
    setFormData({ ...formData, lines: newLines });
  };
  const updateLine = (
    lineID: number,
    field: 'spat_amount' | 'line_length',
    value: string,
  ) => {
    let newLines = [...formData.lines];
    const i = newLines.findIndex(x => x.line_id === lineID);
    if (i < 0) return;
    newLines[i][field] = value.length <= 0 ? null : Number(value);
    setFormData({ ...formData, lines: newLines });
  };
  const updateForm = (key: keyof IFormData, value: any) => {
    if (['drop', 'spacing', 'submersion'].includes(key)) {
      const val = value.length <= 0 ? null : Number(value);
      setFormData(prv => ({ ...prv, [key]: val }));
    } else if (key === 'comment') {
      setFormData(prv => ({
        ...prv,
        comment: value.length <= 0 ? null : value,
      }));
    } else {
      setFormData(prv => ({ ...prv, [key]: value }));
    }
  };
  const showError = (message: string) =>
    dispatch(
      showFeedback({
        isMessageModal: true,
        message,
        type: 'error',
        duration: -1,
      }),
    );
  const validFormData = () => {
    if (formData.lines.length === 0) {
      showError(translate(lang, '_sel_seeded_lines'));
      return null;
    }
    let amountSum = 0;
    for (let ln of formData.lines) {
      const line = linesData.find(x => x.id === ln.line_id);
      if (!line) continue;

      if (!ln.spat_amount) {
        showError(
          translate(
            lang,
            'Please fill spat amount of %s - %s',
            line.farm_name,
            line.line_name,
          ),
        );
        return null;
      }
      if (!ln.line_length) {
        showError(
          translate(
            lang,
            'Please fill line length of %s - %s',
            line.farm_name,
            line.line_name,
          ),
        );
        return null;
      }
      amountSum += ln.spat_amount;
    }
    const availableDisplayWeight = spatStorage.available_weight ?? 0;
    if (amountSum > availableDisplayWeight) {
      showError(
        translate(
          lang,
          'Maximum amount of spat is %s %s',
          availableDisplayWeight,
          getUnitOfSpecie(unitType, 'weight_kg'),
        ),
      );
      return null;
    }
    if (formData.planned_date_seed > formData.planned_date_harvest) {
      showError(translate(lang, '_seeding_date_must_before'));
      return null;
    }
    if (!formData.season_name) {
      showError(translate(lang, 'Please select season'));
      return null;
    }
    if (!formData.spat_size) {
      showError(translate(lang, '_fill_spat_size'));
      return null;
    }
    if (formData.spat_size_max && formData.spat_size_max < formData.spat_size) {
      showError(
        translate(
          lang,
          'Maximum value of spat size must be greater than minimum value',
        ),
      );
      return null;
    }
    if (
      formData.density &&
      formData.density_max &&
      formData.density_max < formData.density
    ) {
      showError(
        translate(
          lang,
          'Maximum value of density must be greater than minimum value',
        ),
      );
      return null;
    }
    return {
      ...formData,
      spat_storage_id: spatStorage.id,
      planned_date_seed: toSecond(formData.planned_date_seed),
      planned_date_harvest: toSecond(formData.planned_date_harvest),
    };
  };
  const confirmClick = async () => {
    const validData = validFormData();
    if (!validData) return;

    setDisabled(true);
    if (overConfirm) {
      const r = await overConfirm(validData);
      if (r === true) {
        dispatch(loadSpatStorages());
        onClose();
      }
      return;
    }
    const res = await sendSingleRequest(
      validData,
      'POST',
      'api/farm/line/multiline-seeding',
      true,
    );
    if (res.status) {
      dispatch(loadSpatStorages());
      dispatch(
        showFeedback({
          isMessage: true,
          message: res.data?.message ?? 'Success',
          type: 'success',
          duration: 6000,
        }),
      );
      onClose(res);
    } else {
      showError(res.data?.message ?? 'Server error');
      setDisabled(false);
    }
  };

  const formLinesColumn: any = [
    {
      title: 'Farm - Line',
      key: 'line_id',
      render: (x: ILineForm) => {
        const line = linesData.find(l => l.id === x.line_id);
        return line ? `${line.farm_name} - ${line.line_name}` : '';
      },
    },
    {
      title: 'Spat amount',
      key: 'spat_amount',
      render: (x: ILineForm) => (
        <Input
          label=''
          type='number'
          value={numToStr(x.spat_amount)}
          onChange={e => updateLine(x.line_id, 'spat_amount', e.target.value)}
          unit={getUnitOfSpecie(unitType, 'weight_kg')}
        />
      ),
    },
    {
      title: 'Longline length / Droppers count',
      key: 'line_length',
      render: (x: ILineForm) => {
        const line = linesData.find(l => l.id === x.line_id);
        const isLongline = line?.line_type === 'LONGLINE';
        const isDropper =
          line?.line_type === 'DROPPERS' || line?.line_type === 'PLATFORM';
        return (
          <Input
            label=''
            type='number'
            value={numToStr(x.line_length)}
            onChange={e => updateLine(x.line_id, 'line_length', e.target.value)}
            unit={
              isLongline
                ? getUnitOfSpecie(unitType, 'distance_m')
                : isDropper
                  ? 'droppers'
                  : undefined
            }
          />
        );
      },
      align: 'left',
    },
  ];

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      className={className}
      closable
      closeIcon={<CloseIcon />}
      width={900}
      footer={null}
    >
      <div className='section-modal wrap'>
        <div className='mb-17'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, '_batches_seeding_lines')}
          </Subtitle>
        </div>
        <div className='section'>
          <div className='mb-17'>
            <Dropdown
              mode='multiple'
              label={translate(lang, 'Select seeded lines')}
              options={linesOption}
              value={formData.lines.map(x => x.line_id.toString()) as any}
              onChange={(v: any) => selectLines(v)}
              showSearch={true}
            />
          </div>
          <Table
            rowKey={x => x.line_id}
            className={'table table--units table--small'}
            pagination={false}
            columns={formLinesColumn}
            dataSource={formData.lines}
          />
          {formData.lines.length > 0 &&
            (() => {
              const longlinesTotal = formData.lines
                .filter(
                  c =>
                    linesData.find(l => l.id === c.line_id)?.line_type ===
                    'LONGLINE',
                )
                .reduce((p, c) => p + (c.line_length ?? 0), 0);
              const droppersTotal = formData.lines
                .filter(
                  c =>
                    linesData.find(l => l.id === c.line_id)?.line_type !==
                    'LONGLINE',
                )
                .reduce((p, c) => p + (c.line_length ?? 0), 0);
              const spatTotal = formData.lines.reduce(
                (p, c) => p + (c.spat_amount ?? 0),
                0,
              );
              const lengthText = [
                longlinesTotal > 0
                  ? `${longlinesTotal} ${getUnitOfSpecie(unitType, 'distance_m')}`
                  : '',
                droppersTotal > 0
                  ? `${droppersTotal} ${translate(lang, 'droppers')}`
                  : '',
              ]
                .filter(x => x)
                .join(' + ');
              return (
                <div className='mt-7 text-right mr-17' style={{ fontSize: 16 }}>
                  {componentTranslate(
                    lang,
                    'Total %s, %s are going to seed',
                    <strong>{lengthText}</strong>,
                    <strong>{`${spatTotal} ${getUnitOfSpecie(unitType, 'weight_kg')}`}</strong>,
                  )}
                </div>
              );
            })()}
        </div>
        <div className='section'>
          <div className='d-flex justify-between-align-end mb-17 col-gap-20'>
            <div className='flex-1'>
              <SelectBoat
                value={formData.boat_id}
                onChange={v => updateForm('boat_id', v)}
              />
            </div>
            <div className='flex-1'>
              <Dropdown
                label={translate(lang, 'Season')}
                options={seasons.map(x => ({
                  id: x.name,
                  value: x.name,
                  label: x.name,
                }))}
                value={formData.season_name}
                onChange={v => updateForm('season_name', v)}
              />
            </div>
          </div>
          <div className='d-flex justify-between-align-end mb-17 col-gap-20'>
            <div className='flex-1'>
              <Datepicker
                label={translate(lang, 'Seeding date')}
                defaultValue={formData.planned_date_seed}
                onChange={e =>
                  e && updateForm('planned_date_seed', e.toDate().getTime())
                }
              />
            </div>
            <div className='flex-1'>
              <Datepicker
                label={translate(lang, 'Planned harvest date')}
                defaultValue={formData.planned_date_harvest}
                onChange={e =>
                  e && updateForm('planned_date_harvest', e.toDate().getTime())
                }
              />
            </div>
          </div>
          <div className='d-flex justify-between-align-end mb-17 col-gap-20'>
            <MinMaxInputForm
              className='flex-1'
              label={translate(lang, 'Spat Size')}
              min={formData.spat_size ?? 0}
              max={formData.spat_size_max}
              onMinChange={v => updateForm('spat_size', v)}
              onMaxChange={v => updateForm('spat_size_max', v)}
              unit={getUnitOfSpecie(unitType, 'size_mm')}
            />
            <MinMaxInputForm
              className='flex-1'
              label={translate(lang, 'Density')}
              min={formData.density ?? 0}
              max={formData.density_max}
              onMinChange={v => updateForm('density', v)}
              onMaxChange={v => updateForm('density_max', v)}
              unit={getUnitOfSpecie(
                unitType,
                getDensityUnitSpecie(formData.density_unit),
              )}
              additionalElement={
                <>
                  <Radio.Group
                    onChange={e => updateForm('density_unit', e.target.value)}
                    value={formData.density_unit}
                  >
                    <RadioButton
                      label={getUnitOfSpecie(unitType, 'spat_mer_m')}
                      value='spat per m'
                    />
                    <RadioButton
                      label={getUnitOfSpecie(unitType, 'kg_per_m')}
                      value='kgs per m'
                    />
                  </Radio.Group>
                </>
              }
            />
          </div>
          <div className='d-flex justify-between-align-end mb-17 col-gap-20'>
            <div className='flex-1'>
              <Input
                label={translate(lang, 'Drop')}
                type='number'
                value={numToStr(formData.drop)}
                onChange={e => updateForm('drop', e.target.value)}
                unit={getUnitOfSpecie(unitType, 'distance_m')}
              />
            </div>
            <div className='flex-1'>
              <Input
                label={translate(lang, 'Spacing')}
                type='number'
                value={numToStr(formData.spacing)}
                onChange={e => updateForm('spacing', e.target.value)}
                unit={getUnitOfSpecie(unitType, 'size_mm')}
              />
            </div>
            <div className='flex-1'>
              <Input
                label={translate(lang, 'Submersion')}
                type='number'
                value={numToStr(formData.submersion)}
                onChange={e => updateForm('submersion', e.target.value)}
                unit={getUnitOfSpecie(unitType, 'distance_m')}
              />
            </div>
          </div>
          <div className='mb-17 mt-17'>
            <Input
              label={translate(lang, 'Comment')}
              type='textarea'
              value={formData.comment ?? ''}
              onChange={e => updateForm('comment', e.target.value)}
            />
          </div>
        </div>
        <div className='modal-button d-flex justify-end'>
          <Button
            width='small'
            size={2}
            type='fill'
            color='red'
            className='rsp-btn'
            onClick={onClose}
            disabled={disabled}
          >
            {translate(lang, 'Cancel')}
          </Button>
          <Button
            width='small'
            size={2}
            type='fill'
            color='green'
            className='rsp-btn ml-16'
            onClick={confirmClick}
            disabled={disabled}
          >
            {translate(lang, 'Confirm')}
          </Button>
        </div>
      </div>
      <ModalFeedbackView />
    </Modal>
  );
};

export default MusselBulkSeedModal;
